import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";

interface SheetProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

function SheetHeader({
  title,
  description,
  onClose,
}: {
  title: string;
  description: string | undefined;
  onClose: () => void;
}): JSX.Element {
  return (
    <header className="flex items-start justify-between gap-3 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-4">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar painel"
        className="rounded-md p-1.5 text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground"
      >
        <X size={16} />
      </button>
    </header>
  );
}

/** Painel lateral (mesmo padrao visual do CopyGeneratorDrawer), fecha no Esc. */
export function Sheet({
  open,
  title,
  description,
  onClose,
  children,
  footer,
}: SheetProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-background/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-lg flex-col border-l border-border bg-card shadow-xl">
        <SheetHeader title={title} description={description} onClose={onClose} />
        <div className="flex-1 space-y-6 overflow-y-auto p-5">{children}</div>
        {footer && <footer className="border-t border-border px-5 py-3">{footer}</footer>}
      </aside>
    </div>
  );
}

interface SectionProps {
  readonly title: string;
  readonly hint?: string;
  readonly children: ReactNode;
}

export function SheetSection({ title, hint, children }: SectionProps): JSX.Element {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="label-editorial">{title}</h3>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}
